/**
 * Declaring a row's cells, once for both kinds of collection.
 *
 * An array row and a record row are declared the same way — sanitizer, initial value, ownership,
 * validators, the composed conditions, the async runners — and the two managers each wrote it. They
 * had already drifted: only one of them told the form the row *owns* its cells, so the sentence
 * `MdyCollectionHost` states about ownership was true of arrays and not of records.
 *
 * The visit is recursive over the row's own shape. What it does about a collection *inside* a row
 * is the caller's answer, handed in as `onCollection`, because that is the part the two kinds do
 * not share and the part that is still being built.
 */
import { composeConditions } from "../conditions.js";
import { isRecord } from "../record-utils.js";
import { hasRequiredMarker } from "../schema-utils.js";
/** Owner key for validators a collection registers on behalf of its rows. */
export const ROW_SCHEMA_KEY = "mdy-schema";
/**
 * Whether the row template declares the cell this path names.
 *
 * A draft is written flat and read back flat, so the *path* is the instruction: `lines.a.sku` is a
 * cell of row `a`, and a row named by a path that does not exist is created to receive it — which is
 * how a saved order gets its lines back. That makes an extra segment an instruction too. `lines.a.b.sku`
 * asks for a row `a` holding a `b` holding a `sku`, and nothing in the document ever described a `b`:
 * built anyway, the collection holds a row of a shape its own template does not have, and the form
 * calls itself valid because there is no field there to be invalid.
 *
 * So the remainder is walked against the template. A nested collection answers for its own subtree —
 * its rows do not exist yet either, and its own manager applies this same rule when they arrive.
 */
export function rowDeclaresCell(item, rest) {
    if (rest.length === 0)
        return true;
    let node = item;
    for (const segment of rest.split(".")) {
        if (node === undefined)
            return false;
        if (node.kind === "record" || node.kind === "array")
            return true;
        if (node.kind !== "group")
            return false;
        node = node.children[segment];
    }
    return node !== undefined;
}
export function registerRowNode(deps, fullPath, node, value, rowPath, sections = []) {
    const { engine } = deps;
    if (node.kind === "array" || node.kind === "record") {
        deps.onCollection(fullPath, node, value);
        return;
    }
    if (node.kind === "field") {
        if (node.sanitize !== null)
            engine.setSanitizer(fullPath, node.sanitize);
        // A row's cells are declared by the same descriptor the schema walk reads at the top, and a
        // secret in a row is the one most worth keeping out of storage: a row is data, so its path is
        // one nobody could have written into an `exclude` list before the user created it.
        if (node.sensitive)
            engine.markSensitive?.(fullPath);
        // A row's cells declare their kind's shape too: a draft naming a row is the same untrusted input
        // as one naming a root field, and the check that guards it needs the same thing to check against.
        if (node.shape !== null)
            engine.declareShape?.(fullPath, node.shape);
        engine.setInitialValue(fullPath, value === undefined ? node.initial : value);
        engine.getField(fullPath);
        // The row declared it. A control showing it may come and go; the row is what ends it.
        engine.ownField(fullPath);
        engine.upsertValidators(fullPath, ROW_SCHEMA_KEY, node.validators, node.validators.some((fn) => hasRequiredMarker(fn)));
        // Its own condition and every section above it, composed once by `conditions.ts` — the same
        // sentence the schema registration uses. The sections are already bound to what they read, a
        // section above a collection knowing the form rather than the row, so they take no arguments.
        const conditions = sections.map((holds) => ({
            holds: () => holds(),
            read: () => ({ value: null, enclosing: {} }),
        }));
        if (node.when !== null) {
            const when = node.when;
            conditions.push({
                holds: when,
                read: () => {
                    const row = deps.readRow(rowPath);
                    return {
                        value: engine.getField(fullPath)?.().value(),
                        enclosing: isRecord(row) ? row : {},
                        root: deps.readRoot?.() ?? {},
                    };
                },
            });
        }
        if (conditions.length > 0) {
            engine.setInactive(fullPath, composeConditions(deps.rx, conditions));
        }
        if (node.asyncValidators.length > 0) {
            engine.upsertAsyncValidators(fullPath, ROW_SCHEMA_KEY, node.asyncValidators, {
                debounceMs: node.asyncDebounceMs,
                dependsOn: node.asyncDependsOn,
                timeoutMs: node.asyncTimeoutMs,
                when: node.asyncWhen ?? undefined,
            });
        }
        return;
    }
    const rec = isRecord(value) ? value : {};
    // A section inside a row: its children answer to it as well as to everything above it.
    const nested = node.when !== null
        ? [
            ...sections,
            () => node.when(deps.readNode(fullPath, node), deps.rowValue(rowPath)),
        ]
        : sections;
    for (const [key, child] of Object.entries(node.children)) {
        registerRowNode(deps, `${fullPath}.${key}`, child, rec[key], rowPath, nested);
    }
}
/**
 * Ends every collection at or under `prefix`, and takes its field with it.
 *
 * The same act for a keyed collection and a positional one: what differs between the two managers is
 * how a row is addressed, and this reaches them by path, which is the one address both agree on. The
 * collection's own field is registered so that collection-level errors have somewhere to surface, so
 * it has to go too — left behind it outlives the row and reads as a value.
 */
export function destroyNestedUnder(nested, engine, prefix) {
    for (const [path, manager] of [...nested]) {
        if (path === prefix || path.startsWith(`${prefix}.`)) {
            manager.destroy();
            nested.delete(path);
            engine.disownField(path);
            engine.removeField(path);
        }
    }
}
/**
 * The collection at `path`, however deep — asking each collection above it in turn.
 *
 * A collection owns the paths directly under it and nothing further: the one two levels down belongs
 * to the one between, so the search descends rather than reading a flat table. Which is why there is
 * no flat table to go stale.
 */
export function nestedAt(nested, path) {
    const own = nested.get(path);
    if (own)
        return own;
    for (const [at, manager] of nested) {
        if (path.startsWith(`${at}.`)) {
            const found = manager.nested(path);
            if (found)
                return found;
        }
    }
    return undefined;
}
